import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlarmClock, ChevronLeft, ChevronRight, Copy, RefreshCw, ShieldAlert } from 'lucide-react'
import {
  useConfirmLateNotice,
  useConfirmWarningWitness,
  useGetLateness,
  useMakeLatenessLink,
  useRecordLateNotice,
  useReviewHandover,
} from '../hooks/backend/lateness'
import { useCapabilities } from '../hooks/useCapabilities'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { Textarea } from '../lib/shadcn/textarea'
import { toast } from '../lib/shadcn/sonner'
import { Chip } from '../components/interviews/Chip'
import { LatenessCaseDialog } from '../components/lateness/LatenessCaseDialog'
import { writeErrorMessage } from '../utils/writeError'
import { dayLabel, shiftWeek } from '../utils/drivingConduct'
import {
  CARTRACK_LABEL,
  SOURCE_LABEL,
  STATUS_LABEL,
  STEP_LABEL,
  lateLine,
  minutes,
  type Handover,
  type LatenessPage,
} from '../utils/lateness'

/**
 * Lateness: handover lateness under clauses 11.1 to 11.11 of the v6 agreement,
 * Addendum No. 1 clause 4.3B and section 6 of the Code of Conduct.
 *
 * Every figure here comes from lateness_page_v1 (db/migrations/1104, 1105): the
 * partner's Late Shift Submission checked against Cartrack at the handover
 * point, the Running late notices, and the cases on the shared conduct record.
 * This page decides nothing. It is where a manager rules on a handover the
 * evidence does not settle, confirms a driver's reason, and records the steps
 * of a case.
 *
 * THE LATENESS CHARGE STAYS IN PAYROLL, on Luyanda's word of 1 October 2026:
 * payroll_v5_lines_v1 moves the lateness rate from the late driver to the
 * partner who waited. This page neither adds nor removes any of it; the
 * minutes it shows are the evidence, not the charge.
 *
 * THE PLATFORM NEVER ISSUES A WARNING. A lateness raises a case and a task, and
 * only for a driver who signed v6 or Addendum No. 1; everyone else is coaching
 * only.
 */

type Tab = 'week' | 'review' | 'notices' | 'cases' | 'warnings' | 'drivers' | 'rate'

function handoverIso(h: Handover): string {
  return `${h.date}T${h.time}:00+02:00`
}

function EvidenceCell({ h }: { h: Handover }) {
  return (
    <div className="space-y-0.5 text-xs">
      <p>Form: {h.form_bucket ?? '—'}{h.borderline && h.late_role ? ' (borderline)' : ''}</p>
      <p>
        Cartrack: {h.cartrack_status ? CARTRACK_LABEL[h.cartrack_status] ?? h.cartrack_status : '—'}
        {h.cartrack_arrival ? ` ${h.cartrack_arrival}` : ''}
        {h.cartrack_collected ? ` ${h.cartrack_collected}` : ''}
        {h.cartrack_minutes !== null ? ` (${minutes(h.cartrack_minutes)})` : ''}
      </p>
      {h.form_partner_matches === false && <p className="text-amber-700 dark:text-amber-400">Form names {h.form_partner_name}, not {h.outgoing}</p>}
    </div>
  )
}

function HandoverStatus({ h }: { h: Handover }) {
  return (
    <div className="flex flex-wrap gap-1">
      {h.needs_review && <Chip>Needs a ruling</Chip>}
      {h.excused && <Chip>Excused</Chip>}
      {h.notice_pending && <Chip>Notice to confirm</Chip>}
      {h.absence && <Chip>Over 2 hours: absence, not a step</Chip>}
      {h.absence_possible && <Chip>Possible absence</Chip>}
      {h.late_role && !h.step_eligible && <Chip>Coaching only</Chip>}
      {h.evidence_source && <Chip>{SOURCE_LABEL[h.evidence_source] ?? h.evidence_source}</Chip>}
    </div>
  )
}

export default function Lateness() {
  const { loading: accessLoading, canView } = useCapabilities()
  const [week, setWeek] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('week')
  const [openCase, setOpenCase] = useState<string | null>(null)
  const [ruling, setRuling] = useState<{ h: Handover; decision: string; role: string; minutes: string; note: string } | null>(null)
  const [phoneNotice, setPhoneNotice] = useState<{ h: Handover; reason: string; at: string; evidence: string } | null>(null)
  const [noticeNote, setNoticeNote] = useState<Record<string, string>>({})
  const [link, setLink] = useState<{ name: string; url: string; note: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const page = useGetLateness<LatenessPage>()
  const review = useReviewHandover()
  const confirm = useConfirmLateNotice()
  const record = useRecordLateNotice()
  const witness = useConfirmWarningWitness()
  const makeLink = useMakeLatenessLink()
  const trigger = page.trigger
  const load = useCallback(() => {
    trigger({ week_start: week }, { skipCache: true })
  }, [trigger, week])
  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  const d = page.data
  const theCase = useMemo(() => d?.cases.find((c) => c.id === openCase) ?? null, [d, openCase])

  async function act(label: string, fn: () => Promise<unknown>, after?: () => void) {
    setBusy(true)
    try {
      await fn()
      toast.success(label)
      after?.()
      load()
    } catch (e) {
      toast.error(writeErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <ShieldAlert className="h-6 w-6 text-[hsl(var(--brand))]" />
          <p className="text-sm font-medium text-foreground">
            Lateness is for Fleetly staff. If you should be able to see it, ask an admin to give your login view access.
          </p>
        </div>
      </div>
    )
  }

  const counts: Record<Tab, number | null> = d
    ? {
        week: d.this_week.length,
        review: d.awaiting_review.length,
        notices: d.notices_pending.length,
        cases: d.cases.filter((c) => c.status !== 'decided' && c.status !== 'withdrawn').length,
        warnings: d.warnings_awaiting_signature.length + d.refusals_awaiting_witness.length,
        drivers: d.drivers.length,
        rate: null,
      }
    : { week: null, review: null, notices: null, cases: null, warnings: null, drivers: null, rate: null }
  const TABS: { id: Tab; label: string }[] = [
    { id: 'week', label: 'This week' },
    { id: 'review', label: 'Needs a ruling' },
    { id: 'notices', label: 'Running late notices' },
    { id: 'cases', label: 'Cases' },
    { id: 'warnings', label: 'Warnings' },
    { id: 'drivers', label: 'Drivers and links' },
    { id: 'rate', label: 'On time rate' },
  ]

  const handoverTable = (rows: Handover[], withRuling: boolean) => (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-2 pr-3">Handover</th>
            <th className="py-2 pr-3">Outgoing / incoming</th>
            <th className="py-2 pr-3">Late</th>
            <th className="py-2 pr-3">Evidence</th>
            <th className="py-2 pr-3">Notice</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((h) => (
            <tr key={h.incoming_shift_id} className="border-b border-border align-top">
              <td className="py-2 pr-3 tabular-nums">
                {dayLabel(h.date)} {h.time}
                <div className="text-xs text-muted-foreground">{h.plate}</div>
              </td>
              <td className="py-2 pr-3">
                {h.outgoing}
                <div className="text-xs text-muted-foreground">to {h.incoming}</div>
              </td>
              <td className="py-2 pr-3">{lateLine(h)}</td>
              <td className="py-2 pr-3">
                <EvidenceCell h={h} />
              </td>
              <td className="py-2 pr-3 text-xs">
                {h.notice ? (
                  <>
                    {h.notice.reason}, {h.notice.at}
                    <div className="text-muted-foreground">
                      {h.notice.before ? 'before the handover' : 'after the handover, does not excuse'}
                      {h.notice.confirmation ? `, ${h.notice.confirmation.replace('_', ' ')}` : ''}
                    </div>
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td className="py-2 pr-3">
                <HandoverStatus h={h} />
                {h.coaching_only_reason && <p className="mt-1 text-xs text-muted-foreground">{h.coaching_only_reason}</p>}
              </td>
              <td className="space-y-1 py-2 text-right">
                {withRuling && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setRuling({ h, decision: '', role: h.late_role ?? '', minutes: h.minutes_late ? String(h.minutes_late) : '', note: '' })
                    }
                  >
                    Rule
                  </Button>
                )}
                {h.late_driver_id && !h.notice && (
                  <Button size="sm" variant="ghost" onClick={() => setPhoneNotice({ h, reason: '', at: '', evidence: '' })}>
                    He told me
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Nothing here.</p>}
    </div>
  )

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <AlarmClock className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Lateness</h1>
          <div className="ml-auto flex items-center gap-1">
            <Button size="sm" variant="outline" aria-label="Previous week" disabled={!d} onClick={() => d && setWeek(shiftWeek(d.week_start, -1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[11rem] text-center text-sm font-medium tabular-nums">
              {d ? `${dayLabel(d.week_start)} to ${dayLabel(d.week_end)}` : 'This week'}
            </span>
            <Button size="sm" variant="outline" aria-label="Next week" disabled={!d} onClick={() => d && setWeek(shiftWeek(d.week_start, 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setWeek(null)}>
              This week
            </Button>
            <Button size="sm" variant="ghost" aria-label="Refresh" onClick={load} disabled={page.loading}>
              <RefreshCw className={'h-4 w-4 ' + (page.loading ? 'animate-spin' : '')} />
            </Button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={
                'rounded px-3 py-1 text-sm font-medium transition-colors ' +
                (tab === t.id ? 'bg-[hsl(var(--brand))] text-white' : 'text-muted-foreground hover:text-foreground')
              }
            >
              {t.label}
              {counts[t.id] ? ` (${counts[t.id]})` : ''}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4 p-4">
        {page.error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">{String(page.error)}</div>}
        {!d && !page.error && (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        )}
        {d && (
          <>
            {(d.switches.signatures_recorded === 0 || !d.switches.messages_send) && (
              <div className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                {d.switches.signatures_recorded === 0 && (
                  <p>
                    No driver has a signed v6 or Addendum No. 1 on record, so every lateness is coaching only and no case is raised. Pay
                    treatment under clause 6.10 applies to everyone.
                  </p>
                )}
                {!d.switches.messages_send && (
                  <p>
                    Lateness emails are switched off. Running late notices, records, warnings and PINs are written and held, never sent.
                  </p>
                )}
              </div>
            )}

            {tab === 'week' && handoverTable(d.this_week, true)}

            {tab === 'review' && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  The partner&rsquo;s form and Cartrack disagree by more than 15 minutes, or only Cartrack shows lateness. No step is raised until you
                  rule. Cartrack alone is never enough: it can mistake a handover elsewhere, or the incoming driver&rsquo;s own trip home, for an
                  arrival.
                  {d.awaiting_review_older > 0 && ` ${d.awaiting_review_older} older handovers also wait for a ruling.`}
                </p>
                {handoverTable(d.awaiting_review, true)}
              </div>
            )}

            {tab === 'notices' && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  A valid reason, given before the handover time and confirmed by the driver&rsquo;s Driver Servant, excuses the lateness. Confirm only
                  what you can stand behind.
                </p>
                {d.notices_pending.length === 0 && <p className="text-sm text-muted-foreground">No notices wait for confirmation.</p>}
                {d.notices_pending.map((n) => (
                  <div key={n.id} className="space-y-2 rounded-md border border-border p-3 text-sm">
                    <p className="font-medium">
                      {n.driver}: {n.handover} handover
                    </p>
                    <p>
                      {n.reason}
                      {n.expected_minutes ? `, about ${n.expected_minutes} minutes` : ''}. Given {n.given},{' '}
                      {n.before ? 'before the handover.' : 'after the handover, so it does not excuse.'}
                      {n.note ? ` "${n.note}"` : ''}
                    </p>
                    {n.evidence && <p className="text-muted-foreground">Proof: {n.evidence}</p>}
                    <Textarea
                      placeholder="Note (needed if you do not confirm)"
                      value={noticeNote[n.id] ?? ''}
                      onChange={(e) => setNoticeNote((x) => ({ ...x, [n.id]: e.target.value }))}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => act('Confirmed.', () => confirm.trigger({ notice_id: n.id, confirmed: true, note: noticeNote[n.id] ?? null }))}
                      >
                        Confirm the reason
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => act('Recorded as not confirmed.', () => confirm.trigger({ notice_id: n.id, confirmed: false, note: noticeNote[n.id] ?? null }))}
                      >
                        Not confirmed
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'cases' && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="py-2 pr-3">Driver</th>
                      <th className="py-2 pr-3">Day</th>
                      <th className="py-2 pr-3">Ladder gives</th>
                      <th className="py-2 pr-3">Where it is</th>
                      <th className="py-2 pr-3">Next</th>
                      <th className="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {d.cases.map((c) => (
                      <tr key={c.id} className="border-b border-border">
                        <td className="py-2 pr-3">{c.driver}</td>
                        <td className="py-2 pr-3 tabular-nums">{dayLabel(c.day)}</td>
                        <td className="py-2 pr-3">{STEP_LABEL[c.step]}</td>
                        <td className="py-2 pr-3">{STATUS_LABEL[c.status]}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{c.next_action ?? '—'}</td>
                        <td className="py-2 text-right">
                          <Button size="sm" variant="outline" onClick={() => setOpenCase(c.id)}>
                            Open
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {d.cases.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No lateness cases.</p>}
              </div>
            )}

            {tab === 'warnings' && (
              <div className="space-y-4">
                <section className="space-y-2">
                  <h2 className="text-sm font-semibold">Waiting for the driver&rsquo;s acknowledgement</h2>
                  {d.warnings_awaiting_signature.length === 0 && <p className="text-sm text-muted-foreground">None.</p>}
                  {d.warnings_awaiting_signature.map((w) => (
                    <div key={w.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2 text-sm">
                      <span className="font-medium">{w.driver}</span>
                      <span>{STEP_LABEL[w.sanction] ?? w.sanction}, given {w.given_on}</span>
                      {w.overdue ? <Chip>Not signed by {w.sign_due_on}</Chip> : <Chip>Due {w.sign_due_on}</Chip>}
                      <Button size="sm" variant="outline" className="ml-auto" onClick={() => setOpenCase(w.case_id)}>
                        Open the case
                      </Button>
                    </div>
                  ))}
                </section>
                <section className="space-y-2">
                  <h2 className="text-sm font-semibold">Refusals waiting for the witness</h2>
                  {d.refusals_awaiting_witness.length === 0 && <p className="text-sm text-muted-foreground">None.</p>}
                  {d.refusals_awaiting_witness.map((w) => (
                    <div key={w.id} className="space-y-1 rounded-md border border-border p-2 text-sm">
                      <p>
                        <span className="font-medium">{w.driver}</span>, {STEP_LABEL[w.sanction] ?? w.sanction} given {w.given_on}.{' '}
                        {w.kind === 'refused_in_person' ? 'Refused to sign in person' : 'Did not sign in time'}, recorded by {w.recorded_by}. Witness:{' '}
                        {w.witness}.
                      </p>
                      <p className="text-muted-foreground">{w.note}</p>
                      {w.i_am_witness ? (
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => act('Confirmed as witness.', () => witness.trigger({ warning_id: w.id, note: null }))}
                        >
                          I saw it given to him: confirm
                        </Button>
                      ) : (
                        <p className="text-xs text-muted-foreground">Only {w.witness} can confirm, on their own login.</p>
                      )}
                    </div>
                  ))}
                </section>
              </div>
            )}

            {tab === 'drivers' && (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <h2 className="mb-2 text-sm font-semibold">Each driver&rsquo;s step and lapse dates</h2>
                  <table className="w-full min-w-[640px] text-sm">
                    <thead className="text-left text-xs text-muted-foreground">
                      <tr className="border-b border-border">
                        <th className="py-2 pr-3">Driver</th>
                        <th className="py-2 pr-3">Late, 30 / 90 days</th>
                        <th className="py-2 pr-3">In force</th>
                        <th className="py-2 pr-3">Next lateness gives</th>
                        <th className="py-2 pr-3">Signed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.drivers.map((r) => (
                        <tr key={r.driver_id} className="border-b border-border">
                          <td className="py-2 pr-3">{r.name}</td>
                          <td className="py-2 pr-3 tabular-nums">
                            {r.late_30} / {r.late_90}
                          </td>
                          <td className="py-2 pr-3 text-xs">
                            {(r.in_force ?? []).length === 0
                              ? 'Nothing'
                              : (r.in_force ?? []).map((w, i) => (
                                  <p key={i}>
                                    {STEP_LABEL[w.sanction] ?? w.sanction} from {w.issued_at}, lapses {w.lapses_on ?? '—'}
                                  </p>
                                ))}
                          </td>
                          <td className="py-2 pr-3">{STEP_LABEL[r.next_step]}</td>
                          <td className="py-2 pr-3 text-xs">{r.gate_date ?? 'Not signed: coaching only'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-2">
                  <h2 className="text-sm font-semibold">Driver&rsquo;s link</h2>
                  <p className="text-sm text-muted-foreground">
                    His own private page: Running late, his lateness records, and warnings to acknowledge. Send it to his own number only; anyone
                    holding it acts as him.
                  </p>
                  {link && (
                    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2 text-sm">
                      <span className="font-medium">{link.name}</span>
                      <Input readOnly value={link.url} className="min-w-[18rem] flex-1" />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard?.writeText(link.url).then(
                            () => toast.success('Link copied.'),
                            () => toast.error('Copy it from the box.'),
                          )
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                    {d.all_drivers.map((r) => (
                      <div key={r.driver_id} className="flex items-center gap-2 rounded-md border border-border px-2 py-1 text-sm">
                        <span className="flex-1 truncate">
                          {r.name}
                          <span className="block text-xs text-muted-foreground">
                            {r.servant ?? 'No Driver Servant'}
                            {r.link_made ? (r.link_last_opened ? `, opened ${r.link_last_opened}` : ', link made, not opened') : ''}
                            {!r.has_email ? ', no email for PINs' : ''}
                          </span>
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          onClick={() =>
                            act('Link ready.', async () => {
                              const res = (await makeLink.trigger({ driver_id: r.driver_id }, { skipCache: true })) as {
                                driver: string
                                url: string
                                note: string
                              }
                              setLink({ name: res.driver, url: res.url, note: res.note })
                            })
                          }
                        >
                          Link
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tab === 'rate' && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] text-sm">
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="py-2 pr-3">Week of</th>
                      <th className="py-2 pr-3">Handovers</th>
                      <th className="py-2 pr-3">Late</th>
                      <th className="py-2 pr-3">Waiting for a ruling</th>
                      <th className="py-2 pr-3">Accounted for</th>
                      <th className="py-2 pr-3">On time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.weeks.map((w) => (
                      <tr key={w.week_start} className="border-b border-border tabular-nums">
                        <td className="py-2 pr-3">{dayLabel(w.week_start)}</td>
                        <td className="py-2 pr-3">{w.handovers}</td>
                        <td className="py-2 pr-3">{w.late}</td>
                        <td className="py-2 pr-3">{w.awaiting_review}</td>
                        <td className="py-2 pr-3">{w.accounted}</td>
                        <td className="py-2 pr-3">{w.on_time_rate === null ? '—' : `${w.on_time_rate}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-2 text-xs text-muted-foreground">
                  On time is out of the handovers somebody gave an account of: the partner&rsquo;s Late Shift Submission or a manager&rsquo;s ruling.
                  A handover nobody accounted for is not counted as on time, so a week with none shows a dash. The form&rsquo;s lateness question only
                  has answers from 11 September 2026.
                </p>
              </div>
            )}

            <details className="rounded-md border border-border p-3 text-sm">
              <summary className="cursor-pointer font-medium">Settings in force</summary>
              <ul className="mt-2 space-y-1">
                {d.settings.map((s) => (
                  <li key={s.key}>
                    <span className="font-mono text-xs">{s.key}</span> {s.value}, from {s.from}. <span className="text-muted-foreground">{s.note}</span>
                  </li>
                ))}
              </ul>
            </details>
          </>
        )}
      </div>

      {ruling && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-lg space-y-3 rounded-lg border border-border bg-card p-4">
            <h2 className="font-semibold">
              Rule on the {ruling.h.time} handover of {ruling.h.plate}, {dayLabel(ruling.h.date)}
            </h2>
            <EvidenceCell h={ruling.h} />
            <NativeSelect value={ruling.decision} onChange={(e) => setRuling({ ...ruling, decision: e.target.value })}>
              <option value="">Choose</option>
              <option value="late">Late</option>
              <option value="not_late">Not late</option>
            </NativeSelect>
            {ruling.decision === 'late' && (
              <div className="grid gap-2 sm:grid-cols-2">
                <NativeSelect value={ruling.role} onChange={(e) => setRuling({ ...ruling, role: e.target.value })}>
                  <option value="">Who was late</option>
                  <option value="outgoing">Outgoing: {ruling.h.outgoing}</option>
                  <option value="incoming">Incoming: {ruling.h.incoming}</option>
                </NativeSelect>
                <Input
                  type="number"
                  min={16}
                  placeholder="Minutes late"
                  value={ruling.minutes}
                  onChange={(e) => setRuling({ ...ruling, minutes: e.target.value })}
                />
              </div>
            )}
            <Textarea
              placeholder="What you relied on: what the partner said, what Cartrack shows, what the driver said"
              value={ruling.note}
              onChange={(e) => setRuling({ ...ruling, note: e.target.value })}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRuling(null)}>
                Cancel
              </Button>
              <Button
                disabled={busy}
                onClick={() =>
                  act(
                    'Ruling recorded.',
                    () =>
                      review.trigger({
                        incoming_shift_id: ruling.h.incoming_shift_id,
                        decision: ruling.decision,
                        late_role: ruling.decision === 'late' ? ruling.role : null,
                        minutes: ruling.decision === 'late' ? ruling.minutes : null,
                        note: ruling.note,
                      }),
                    () => setRuling(null),
                  )
                }
              >
                Record the ruling
              </Button>
            </div>
          </div>
        </div>
      )}

      {phoneNotice && d && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-lg space-y-3 rounded-lg border border-border bg-card p-4">
            <h2 className="font-semibold">
              {phoneNotice.h.late_driver} told you he was running late for the {phoneNotice.h.time} handover
            </h2>
            <p className="text-sm text-muted-foreground">
              Give the time of his WhatsApp or call, not the time now. A notice after {phoneNotice.h.time} does not excuse.
            </p>
            <NativeSelect value={phoneNotice.reason} onChange={(e) => setPhoneNotice({ ...phoneNotice, reason: e.target.value })}>
              <option value="">His reason</option>
              {d.options.reasons.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </NativeSelect>
            <Input type="datetime-local" value={phoneNotice.at} onChange={(e) => setPhoneNotice({ ...phoneNotice, at: e.target.value })} />
            <Input
              placeholder='What proves it, e.g. "WhatsApp to Kabelo, 15:41"'
              value={phoneNotice.evidence}
              onChange={(e) => setPhoneNotice({ ...phoneNotice, evidence: e.target.value })}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setPhoneNotice(null)}>
                Cancel
              </Button>
              <Button
                disabled={busy}
                onClick={() =>
                  act(
                    'Notice recorded. Confirm the reason on Running late notices.',
                    () =>
                      record.trigger({
                        driver_id: phoneNotice.h.late_driver_id,
                        handover_at: handoverIso(phoneNotice.h),
                        role: phoneNotice.h.late_role,
                        reason_code: phoneNotice.reason,
                        notified_at: phoneNotice.at ? `${phoneNotice.at}:00+02:00` : '',
                        evidence: phoneNotice.evidence,
                        note: null,
                      }),
                    () => setPhoneNotice(null),
                  )
                }
              >
                Record the notice
              </Button>
            </div>
          </div>
        </div>
      )}

      {d && (
        <LatenessCaseDialog
          c={theCase}
          page={d}
          onClose={() => setOpenCase(null)}
          onChanged={() => {
            load()
          }}
        />
      )}
    </div>
  )
}

